<script lang="ts" setup>
import { AppButton, AppLogo, AppPassword, AppPasswordCriteria, AppTextField, AppTypography } from '@/core/components';
import type { FormErrors } from '@/core/types/Form';
import { useVuelidate } from '@vuelidate/core';
import { storeToRefs } from 'pinia';
import { ref } from 'vue';
import { useAuthStore } from '../store/auth';
import type { RegisterForm } from '../types/Auth';

const { validations } = useValidation();

const form = ref<RegisterForm>({
  email: '',
  password: '',
  password_confirmation: '',
});
const rules = computed(() => ({
  email: {
    required: validations.required('Email'),
    email: validations.email(),
  },
  password: {
    required: validations.required('Password'),
    minLength: validations.minLength('Password', 8),
    password: validations.password(),
  },
  password_confirmation: {
    required: validations.required('Password Confirmation'),
    sameAs: validations.sameAs('Password Confirmation', 'Password', form.value.password),
  },
}));

const router = useRouter();

const { user } = storeToRefs(useAuthStore());
const v$ = useVuelidate(rules, form, { $autoDirty: true });

const { token, initToken } = await useGenerateReCaptcha('register');
const { setToken } = useAuthState();
const { getSession } = useAuth();
const { data, pending: registerLoading, execute: execSubmit } = useSubmit();

const registerError = ref<{ data: FormErrors<RegisterForm> }>({ data: { errors: {}, message: '' } });
const handleSignUp = async (): Promise<void> => {
  const result = await v$.value.$validate();
  if (!result) return;
  try {
    registerLoading.value = true;
    await execSubmit('auth/register', {
      method: 'POST',
      body: {
        email: form.value.email,
        password: form.value.password,
        password_confirmation: form.value.password_confirmation,
      },
      headers: {
        ...(token.value && { 'X-Recaptcha-Token': token.value }),
      },
    });

    if (data.value) {
      setToken(data.value.data.access_token);
      await getSession();
      user.value = data.value.data.user;
      router.push({ name: 'verify' });
    }
  } catch (error) {
    registerError.value = error as { data: FormErrors<RegisterForm> };
    initToken();
  } finally {
    registerLoading.value = false;
  }
};

const errors = computed(() => {
  return $parsedError<RegisterForm>(form.value, v$, registerError.value?.data?.errors);
});
</script>

<template>
  <v-form @submit.prevent="handleSignUp">
    <ClientOnly>
      <AppLogo src="/logo.png" width="74px" height="74px" class="mb-12" />
      <AppTypography element="h1" type="large" class="mb-8">Sign up</AppTypography>
      <AppTextField
        v-model="form.email"
        data-cy="input-email"
        :error-messages="errors.email"
        :disabled="registerLoading"
        label="Email address"
        data-testid="email"
        class="mb-1"
        autocomplete="email"
      >
      </AppTextField>

      <AppPassword
        v-model="form.password"
        data-cy="input-password"
        label="Password"
        :error-messages="errors.password"
        :disabled="registerLoading"
        data-testid="password"
        class="mb-1"
      >
      </AppPassword>

      <AppPassword
        v-model="form.password_confirmation"
        data-cy="input-password-confirmation"
        label="Confirm password"
        data-testid="password-confirmation"
        :error-messages="errors.password_confirmation"
        :disabled="registerLoading"
        class="mb-1"
      >
      </AppPassword>

      <AppPasswordCriteria :password="form.password" class="mb-8" />

      <AppButton
        data-cy="button-submit"
        block
        class="mt-4 mb-8 font-weight-bold"
        type="submit"
        data-testid="submit"
        :loading="registerLoading"
      >
        Sign up
      </AppButton>

      <AppTypography element="span" type="subheadline-1" class="d-block text-decoration-none mb-8">
        By signing up, I agree to the
        <nuxt-link to="/terms-and-conditions" class="text-primary text-decoration-none">Terms & Conditions</nuxt-link>
        and
        <nuxt-link to="/privacy-policy" class="text-primary text-decoration-none">Privacy Policy</nuxt-link>
      </AppTypography>
      <AppTypography element="span" class="d-block text-center text-decoration-none">
        Don’t have an account yet?
        <nuxt-link to="/login" class="text-primary font-weight-bold text-decoration-none">Sign in</nuxt-link>
      </AppTypography>
    </ClientOnly>
  </v-form>
</template>
